import { useMemo, useState, useEffect, type ReactNode } from "react";

import {
  type FeedbackContextValues,
  type FeedbackState,
  FeedbackContext,
} from "./Feedback.context";

type FeedbackProviderProps = {
  children: ReactNode;
  storageKey?: string;
  thresholds?: number[];
};

const STORAGE_KEY = "feedback-global-count";
const DEFAULT_THRESHOLDS = [9, 45, 117, 189, 306, 495];

const getStoredCount = (key: string): number => {
  if (typeof window === "undefined") return 0;

  try {
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : 0;
  } catch (error) {
    console.warn("Feedback: Failed to read from localStorage:", error);
    return 0;
  }
};

const setStoredCount = (count: number, key: string): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(key, count.toString());
  } catch (error) {
    console.warn("Feedback: Failed to write to localStorage:", error);
    // Continue without persistence - feedback still works in memory
  }
};

export const FeedbackProvider = ({
  children,
  storageKey = STORAGE_KEY,
  thresholds = DEFAULT_THRESHOLDS,
}: FeedbackProviderProps) => {
  const getStoredCountForKey = () => getStoredCount(storageKey);

  const [count, setCount] = useState<number>(getStoredCountForKey);
  const [wasPrompted, setWasPrompted] = useState<boolean>(false);
  const [hasEngaged, setHasEngaged] = useState<boolean>(false);
  const [sessionStartCount] = useState<number>(getStoredCountForKey);

  // Sync count to localStorage whenever it changes
  useEffect(() => {
    setStoredCount(count, storageKey);
  }, [count, storageKey]);

  const contextValue: FeedbackContextValues = useMemo(() => {
    const incrementEngagementCount = () => {
      setCount((prev) => prev + 1);
    };

    const onFeedbackToastDismissed = () => {
      // Only allow dismissal if a threshold was crossed and we haven't been prompted yet
      if (crossedThresholdInSession && !wasPrompted) {
        setWasPrompted(true);
      } else {
        console.warn(
          "Feedback: onFeedbackToastDismissed() called but no active toast to dismiss"
        );
      }
    };

    const onFeedbackEngaged = () => {
      // Only allow engagement if a threshold was actually crossed this session
      if (crossedThresholdInSession) {
        setHasEngaged(true);
        setWasPrompted(true); // Engagement implies toast was shown
      } else {
        console.warn(
          "Feedback: onFeedbackEngaged() called but no threshold crossed this session"
        );
      }
    };

    // Filter out thresholds that were already passed before this session
    const remainingThresholds = thresholds.filter(
      (threshold) => threshold > sessionStartCount
    );

    // Check if any remaining threshold was crossed in this session
    const crossedThresholdInSession = remainingThresholds.some(
      (threshold) => count >= threshold
    );

    // Determine feedback state based on session conditions. If the user has engaged with the
    // feedback at all, no further prompts are needed so this remains null.
    let feedbackState: FeedbackState = null;
    if (crossedThresholdInSession) {
      if (!wasPrompted && !hasEngaged) {
        feedbackState = "toast";
      } else if (wasPrompted && !hasEngaged) {
        feedbackState = "pip";
      }
    }

    return {
      feedbackState,
      incrementEngagementCount,
      onFeedbackToastDismissed,
      onFeedbackEngaged,
    };
  }, [count, wasPrompted, hasEngaged, sessionStartCount, thresholds]);

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}
    </FeedbackContext.Provider>
  );
};
